import {
  useMutation,
  useQuery,
  useQueryClient,
  useSuspenseQuery,
} from "@tanstack/react-query";
import { type ClinicUser, userClient } from "@users/userClient";

export const displayNameOf = (
  users: { id: string; displayName: string }[] | undefined,
  userId: string | null,
  fallback = "Someone",
): string => users?.find((u) => u.id === userId)?.displayName ?? fallback;

const usersKeys = {
  list: (sessionKey: string) => ["users", sessionKey] as const,
  current: (sessionKey: string) => ["users", sessionKey, "current"] as const,
};

/** Non-suspense: the shell renders while it resolves; a failed read retries on focus. */
export const useCurrentUser = (sessionKey: string) =>
  useQuery({
    queryKey: usersKeys.current(sessionKey),
    queryFn: () => userClient.getCurrent(),
    enabled: sessionKey !== "",
  });

/** Non-suspense and best-effort: callers only want an optional display name. */
export const useClinicUsers = (sessionKey: string) =>
  useQuery({
    queryKey: usersKeys.list(sessionKey),
    queryFn: () => userClient.list(),
  });

export const useUsersList = (sessionKey: string) =>
  useSuspenseQuery({
    queryKey: usersKeys.list(sessionKey),
    queryFn: () => userClient.list(),
  });

/** No invalidation: a reset changes nothing the roster renders. */
export const useResetPin = () =>
  useMutation({
    mutationFn: ({ userId, pin }: { userId: string; pin: string }) =>
      userClient.resetPin(userId, pin),
    meta: { action: "reset the PIN" },
  });

export const useCreateUser = (sessionKey: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: userClient.create,
    // AddUserButton shows this failure inline.
    meta: { handled: true },
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: usersKeys.list(sessionKey) }),
  });
};

/** The PUT returns the user, so the list is patched in place. A change to your
 * own roles refetches everything: the signed-in user, and tasks and inbox items
 * addressed to a role. */
export const useSetUserRoles = (sessionKey: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: userClient.setRoles,
    // EditRolesButton shows this failure inline.
    meta: { handled: true },
    onSuccess: (updated) => {
      queryClient.setQueryData<ClinicUser[]>(
        usersKeys.list(sessionKey),
        (users) => users?.map((u) => (u.id === updated.id ? updated : u)),
      );
      if (updated.id === sessionKey) void queryClient.invalidateQueries();
    },
  });
};

export const useRemoveUser = (sessionKey: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: userClient.remove,
    meta: { action: "remove the user" },
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: usersKeys.list(sessionKey) }),
  });
};
